import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { motion } from 'framer-motion';
import { RESET_PASSWORD_MUTATION } from "../graphql/mutations";
import { useMutation } from "@apollo/client";



const ChangePassword = ({ onClose }) => {
  const [otp, setOtp] = useState(new Array(6).fill(''));
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const inputsRef = useRef([]);
  const [ChangePassword] = useMutation(RESET_PASSWORD_MUTATION);

  const phoneNumber = localStorage.getItem('mobile');
  const API_BASE_URL = 'http://localhost:8080';

  useEffect(() => {
    if (inputsRef.current[0]) setTimeout(() => inputsRef.current[0].focus(), 100);
    document.body.style.overflow = 'hidden';
    return () => (document.body.style.overflow = 'auto');
  }, []);

  useEffect(() => {
    if (!isOtpVerified && timer > 0) {
      const countdown = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(countdown);
    } else {
      setCanResend(true);
    }
  }, [timer, isOtpVerified]);

  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(/\D/, '');
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const verifyOtp = async () => {
    if (!otp.every((d) => d)) {
      return setErrorMsg('Please enter the complete OTP.');
    }

    setLoading(true);
    setErrorMsg('');
    try {
      const response = await verifyOtpApi(otp.join(''));
      if (response.success) {
        setSuccessMsg('OTP verified successfully!');
        setTimeout(() => {
          setIsOtpVerified(true);
          setSuccessMsg('');
        }, 800);
      } else {
        setErrorMsg(response.message || 'OTP verification failed.');
      }
    } catch (err) {
      setErrorMsg('Server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = () => {
    setOtp(new Array(6).fill(''));
    setTimer(30);
    setCanResend(false);
    setErrorMsg('');
    setSuccessMsg('');
    inputsRef.current[0]?.focus();
    console.log('Resent OTP');
  };

  const handleChangePassword = async () => {
    setErrorMsg('');
    if (newPassword !== confirmPassword) {
      return setErrorMsg('Passwords do not match.');
    }

    setLoading(true);
    try {
      const response = await changePasswordApi(newPassword);
      if (response.success) {
        setSuccessMsg('Password changed successfully!');
        setTimeout(() => {
          onClose();
        }, 1200);
      } else {
        setErrorMsg(response.message || 'Password change failed.');
      }
    } catch (err) {
      setErrorMsg('Server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const maskPhoneNumber = (phoneNumber) => {
    if (!phoneNumber || phoneNumber.length < 4) return 'XXXXXXXXXX';
    return 'X'.repeat(phoneNumber.length - 4) + phoneNumber.slice(-4);
  };

  // Simulated API (replace with real endpoints)
  const verifyOtpApi = async (otp) => {
    try {
      const mobile = localStorage.getItem('mobile');
      const response = await fetch('https://gcp-poc-with-apollo-1081525845464.asia-south1.run.app/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, otp }),
      });

      const data = await response.json();

      if (data.status === 'error') {
        return { success: false, message: data.message || 'OTP verification failed.' };
      }

      return { success: true };
    } catch (error) {
      return { success: false, message: 'Network error. Please try again.' };
    }
  };


  const changePasswordApi = async (password) => {
    const id = localStorage.getItem('id');
  
    try {
      const { data } = await ChangePassword({
        variables: {
          Id: id,
          New_Password: password,
        },
      });
  
      return { success: true, message: data.changePassword };
    } catch (error) {
      const message =
        error?.graphQLErrors?.[0]?.message ||
        error?.message ||
        'Network error. Please try again.';
  
      return { success: false, message };
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md mx-4 relative p-6"
        style={{
          border: '1px solid #fcd34d',
        }}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            {isOtpVerified ? 'Set New Password' : 'Verify OTP'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <p className="text-center text-gray-600 mb-3">
          +91 <strong>{maskPhoneNumber(phoneNumber)}</strong>
        </p>

        {!isOtpVerified && (
          <>
            <p className="text-sm text-center text-gray-500 mb-2">
              OTP is valid for the next <strong className="text-yellow-500">5 minutes</strong>.
            </p>
            <div className="flex justify-center space-x-2 mb-4">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  ref={(el) => (inputsRef.current[index] = el)}
                  className="w-12 h-12 rounded-lg bg-white border border-gray-300 text-center text-lg shadow-inner focus:ring-2 focus:ring-yellow-400 focus:outline-none"
                />
              ))}
            </div>

            {errorMsg && <p className="text-red-500 text-sm text-center mb-2">{errorMsg}</p>}
            {successMsg && <p className="text-green-600 text-sm text-center mb-2">{successMsg}</p>}

            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={verifyOtp}
              disabled={loading}
              className="w-full py-2 mb-3 rounded-full bg-[#FAD707] text-white font-semibold shadow-md hover:shadow-lg transition disabled:opacity-60"
            >
              {loading ? 'Verifying...' : 'Verify OTP'}
            </motion.button>

            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={resendOtp}
              disabled={!canResend}
              className="w-full text-sm text-center text-[#FAD707] disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              {canResend ? 'Resend OTP' : `Resend in ${timer}s`}
            </motion.button>
          </>
        )}

        {isOtpVerified && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-400 focus:outline-none"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-400 focus:outline-none"
              />
            </div>

            {errorMsg && <p className="text-red-500 text-sm text-center mb-2">{errorMsg}</p>}
            {successMsg && <p className="text-green-600 text-sm text-center mb-2">{successMsg}</p>}

            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={handleChangePassword}
              disabled={loading || !newPassword || !confirmPassword}
              className="w-full py-2 rounded-full bg-[#FAD707] text-white font-semibold shadow-md hover:shadow-lg transition disabled:opacity-60"
            >
              {loading ? 'Saving...' : 'Change Password'}
            </motion.button>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default ChangePassword;
